// Import necessary React hooks and components
import { useState, useMemo, useRef } from "react";
// Import necessary Material-UI components
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Stack,
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { Box } from "@mui/system";
// Import utilities from MUI DataGrid
import { DataGrid } from "@mui/x-data-grid";
// Import lodash library
import _ from "lodash";
// Import custom functions and components
import {
  CustomMUITableContainer,
  formatDate,
  createSearchColumns,
  ExportCsvButton,
} from "../../common";
import { ItemDetailsDialog } from "../../requests/viewRequests/ItemDetailsDialog.component";

// Valid values for the procurementApproval status dropdown.
const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "Accepted", label: "Accepted" },
  { value: "Rejected", label: "Rejected" },
];

// Column configuration: each entry becomes a DataGrid column with an embedded
// per-column search control. `search` picks the control type; the `field` must
// match a key the backend whitelists in SEARCHABLE_FIELDS (approverListShared).
export const COLUMN_CONFIG = [
  {
    field: "procurementApproval",
    headerName: "Procurement Approval",
    minWidth: 200,
    search: "status",
    csvFormat: "startCase",
    statusOptions: STATUS_OPTIONS,
    valueGetter: (params) => params.value && _.startCase(params.value.status),
  },
  { field: "itemERPCode", minWidth: 200, search: "text" },
  { field: "itemDescription", minWidth: 200, search: "text" },
  { field: "specification", minWidth: 200, search: "text" },
  { field: "preferredUnitOfMeasure", minWidth: 200, search: "text" },
  { field: "quantityForSample", minWidth: 200, search: "text" },
  { field: "productClass", minWidth: 200, search: "text" },
  {
    field: "date",
    minWidth: 260,
    search: "date",
    type: "date",
    csvFormat: "date",
    valueFormatter: (params) =>
      params.value !== "pending" ? formatDate(params.value) : params.value,
  },
  {
    field: "needByDate",
    minWidth: 260,
    search: "date",
    type: "date",
    csvFormat: "date",
    valueFormatter: (params) =>
      params.value !== "pending" ? formatDate(params.value) : params.value,
  },
  { field: "purpose", minWidth: 200, search: "text" },
  { field: "endUserLocation", minWidth: 200, search: "text" },
  {
    field: "executiveChefEmail",
    headerName: "Executive Chef Email",
    minWidth: 240,
    search: "text",
  },
  {
    field: "HODDepartment",
    headerName: "HOD Department",
    minWidth: 200,
    search: "text",
  },
  {
    field: "email",
    headerName: "Chef Email",
    minWidth: 240,
    search: "text",
    csvFormat: "naText",
    // Defensive fallback: `email` is required so it should always be present,
    // but render "N/A" rather than a blank cell if it is ever absent.
    valueGetter: (params) => (params.value ? params.value : "N/A"),
  },
  { field: "sampleItemID", minWidth: 200, search: "text" },
  { field: "sampleRequestID", minWidth: 200, search: "text" },
];

/**
 * DelegationListTable component for displaying a table with delegation list
 * @param {Object} props - Component props including data, and callbacks.
 */
export function DelegationListTable({
  data,
  title,
  description,
  filterCheckbox,
  onRowsSelectionHandler,
  rowSelectionModel,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  button,
  approverType,
  setApproverType,
  decoded,
  searchModel = {},
  onSearchChange,
  onClearAllSearch,
  activeFilterCount = 0,
  filterResetKey = 0,
  sortModel = [],
  onSortChange,
  onExport,
}) {
  // State for storing fetched delegation items list data
  const [rowData, setRowData] = useState();
  // State for controlling item details modal visibility
  const [openItemDetailsModal, setOpenItemDetailsModal] = useState(false);
  // Function to open item details modal and set row data
  const handleItemDetailsModalOpen = (data) => {
    setOpenItemDetailsModal(true);
    setRowData(data);
  };

  // Function to handle row click event and open item details modal
  function handleRowClick(event) {
    handleItemDetailsModalOpen(event.row);
  }
  // Function to close item details modal
  const handleItemDetailsModalClose = () => {
    setOpenItemDetailsModal(false);
  };

  // Always-fresh reference to the current search model, so a memoized column's
  // header can seed its control from the latest value without forcing the
  // columns to rebuild on every keystroke.
  const searchModelRef = useRef(searchModel);
  searchModelRef.current = searchModel;

  // Column definitions built by the shared helper (see createSearchColumns).
  // Memoized on the inputs that actually change the header structure so the
  // embedded search inputs are NOT remounted while typing — only when
  // filterResetKey bumps, which is exactly when the filters should reset.
  // `sortModel` is included so the sort icons re-render on sort changes; the
  // header controls keep the same key (field + filterResetKey), so this rebuild
  // updates the icons WITHOUT remounting the search inputs.
  const columnDefinition = useMemo(
    () =>
      createSearchColumns({
        columnConfig: COLUMN_CONFIG,
        searchModelRef,
        onSearchChange,
        filterResetKey,
        sortModel,
        onSortChange,
      }),
    [filterResetKey, onSearchChange, onSortChange, sortModel]
  );

  return (
    <>
      {/* Custom table container with MUI styles */}
      <CustomMUITableContainer
        children={
          <Box>
            <Grid
              container
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Title and description */}

              <Grid item xs={12}>
                <h2 style={{ paddingLeft: "20px" }}>{title}</h2>
                <p style={{ paddingLeft: "20px" }}>{description}</p>
              </Grid>
              <Grid item xs={6}>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  spacing={2}
                  sx={{ marginLeft: "20px", paddingBottom: "20px" }}
                >
                  <FormControl sx={{ minWidth: "150px" }}>
                    {/* Dropdown for selecting approver type */}
                    <InputLabel id="demo-simple-select-label">
                      Approver Type
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={approverType}
                      label="Approver Type"
                      onChange={(e) => setApproverType(e.target.value)}
                      MenuProps={{ PaperProps: { sx: { maxHeight: "15%" } } }}
                    >
                      {decoded && decoded.role === "procurement" && (
                        <MenuItem value="chef">Chef</MenuItem>
                      )}
                      <MenuItem value="executiveChef">Executive Chef</MenuItem>
                      <MenuItem value="hod">HOD</MenuItem>
                    </Select>
                    <FormHelperText>Select The Type Of Approver</FormHelperText>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterAltOffIcon />}
                    onClick={onClearAllSearch}
                    disabled={!activeFilterCount}
                    sx={{ mt: "8px", borderRadius: "10px", whiteSpace: "nowrap" }}
                  >
                    {activeFilterCount
                      ? `Clear Filters (${activeFilterCount})`
                      : "Clear Filters"}
                  </Button>
                  {onExport && (
                    <Box sx={{ mt: "8px" }}>
                      <ExportCsvButton
                        onExport={onExport}
                        rowCount={rowCount}
                        loading={!data}
                      />
                    </Box>
                  )}
                </Stack>
              </Grid>
              {button && button}
            </Grid>
            {/* Render filter checkboxes if provided */}
            {filterCheckbox && filterCheckbox}
            <Box
              sx={{
                "& .detailsColumn": {
                  display: "flex !important",
                  alignItems: "flex-start !important",
                },
              }}
            >
              {/* DataGrid component to display table */}
              <DataGrid
                rows={data ?? []}
                loading={!data}
                autoHeight={true}
                columns={columnDefinition}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                rowCount={rowCount ?? 0}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                getRowHeight={() => "auto"}
                // Header row sized to fit the compact embedded search controls.
                columnHeaderHeight={96}
                disableRowSelectionOnClick
                // Built-in (client-side) filtering / column menu are replaced by
                // the server-driven per-column search embedded in the headers.
                disableColumnFilter
                disableColumnMenu
                // Customize cell class for Details column
                getCellClassName={(params) => {
                  if (params.field === "Details") {
                    return "detailsColumn";
                  }
                }}
                // Handle row click event
                onRowClick={handleRowClick ? (e) => handleRowClick(e) : null}
                checkboxSelection={true}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={(ids) => {
                  onRowsSelectionHandler(ids);
                }}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-columnHeaderTitleContainer": {
                    alignItems: "flex-start",
                  },
                }}
              />
            </Box>
          </Box>
        }
      />

      {/* Render item details dialog if rowData is present */}
      {rowData && (
        <ItemDetailsDialog
          rowData={rowData}
          open={openItemDetailsModal}
          handleClose={handleItemDetailsModalClose}
        />
      )}
    </>
  );
}
